import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getShippingRates, createShippingLabel } from '../services/shippingService';
import { blockWheelOnNumber } from '../utils/numberInput';
import styles from './ShipArtworkModal.module.css';

const packagingOptions = ['Box', 'Tube', 'Envelope'];

const ShipArtworkModal = ({ shipment, onClose, onSuccess }) => {
  // Add phone formatter function with international support
  const formatPhoneNumber = (value) => {
    // Strip all non-numeric characters except +
    const cleaned = value.replace(/[^\d+]/g, '');
    
    // Handle empty input
    if (!cleaned) return '';
    
    // If it starts with +, treat as international
    if (cleaned.startsWith('+')) {
      // Remove the + for processing
      const numbers = cleaned.slice(1);
      
      // Format based on length
      if (numbers.length <= 3) return `+${numbers}`;
      if (numbers.length <= 6) return `+${numbers.slice(0, 3)} (${numbers.slice(3)})`;
      if (numbers.length <= 9) return `+${numbers.slice(0, 3)} (${numbers.slice(3, 6)}) ${numbers.slice(6)}`;
      return `+${numbers.slice(0, 3)} (${numbers.slice(3, 6)}) ${numbers.slice(6, 9)}-${numbers.slice(9, 13)}`;
    }
    
    // If no +, treat as US number
    const numbers = cleaned;
    if (numbers.length <= 3) return `+1 (${numbers}`;
    if (numbers.length <= 6) return `+1 (${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `+1 (${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  // Auto-populate fields from shipment/order
  const [recipient, setRecipient] = useState({
    name: shipment.recipient_name || '',
    phone: shipment.recipient_phone ? formatPhoneNumber(shipment.recipient_phone) : '',
    email: shipment.recipient_email || '',
    address_line1: shipment.recipient_address_line1 || '',
    address_line2: shipment.recipient_address_line2 || '',
    city: shipment.recipient_city || '',
    state: shipment.recipient_state || '',
    postal_code: shipment.recipient_postal_code || '',
    country: shipment.recipient_country || '',
  });
  const [shipFrom, setShipFrom] = useState({
    name: shipment.ship_from_name || '',
    phone: shipment.ship_from_phone || '',
    address_line1: shipment.ship_from_address_line1 || '',
    address_line2: shipment.ship_from_address_line2 || '',
    city: shipment.ship_from_city || '',
    state: shipment.ship_from_state || '',
    postal_code: shipment.ship_from_postal_code || '',
    country: shipment.ship_from_country || '',
  });
  const [dimensions, setDimensions] = useState({
    width: shipment.width || '',
    height: shipment.height || '',
    depth: shipment.depth || '',
  });
  const [weight, setWeight] = useState(shipment.weight || '');
  const [weightUnit, setWeightUnit] = useState(shipment.weight_unit || 'lb');
  const [dimensionUnit, setDimensionUnit] = useState(shipment.dimension_unit || 'in');
  const [packagingType, setPackagingType] = useState(shipment.packaging_type || 'Box');
  const [extraInsurance, setExtraInsurance] = useState(0);
  const [rates, setRates] = useState([]);
  const [saleCoverage, setSaleCoverage] = useState(null);
  const [showFasterOptions, setShowFasterOptions] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [labelUrl, setLabelUrl] = useState(null);
  const [error, setError] = useState(null);

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const modalRef = useRef(null);
  const bodyRef = useRef(null);

  // The server ships to the address on the sale, so editing it here would be misleading.
  const recipientLocked = Boolean(shipment.recipient_address_line1);
  const maxInsurance = Math.max(Number(shipment.amount) || 0, 100);
  const coveredRates = rates.filter(rate => rate.covered);
  const visibleRates = showFasterOptions ? coveredRates : coveredRates.filter(rate => rate.recommended);
  const hiddenRateCount = coveredRates.length - visibleRates.length;

  const formatRateName = (rate) => {
    const carrier = String(rate.carrier || '').trim();
    const service = String(rate.service || '').trim();
    if (!service) return carrier || 'Shipping';
    if (!carrier) return service;
    if (service.toLowerCase().startsWith(carrier.toLowerCase())) return service;
    return `${carrier} ${service}`;
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    if (rates.length > 0 && bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  }, [rates]);

  const validateForm = () => {
    const errors = {};
    if (!recipient.name) errors.recipientName = 'Recipient name is required';
    if (!recipient.address_line1) errors.recipientAddress = 'Recipient address is required';
    if (!recipient.city) errors.recipientCity = 'City is required';
    if (!recipient.state) errors.recipientState = 'State is required';
    if (!recipient.postal_code) errors.recipientPostal = 'Postal code is required';
    if (!recipient.country) errors.recipientCountry = 'Country is required';
    if (!weight) errors.weight = 'Weight is required';
    if (!dimensions.width || !dimensions.height || !dimensions.depth) {
      errors.dimensions = 'All dimensions are required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGetRates = async () => {
    if (!validateForm()) {
      setValidationError('Please fill in all required fields');
      return;
    }
    setValidationError(null);
    setRatesLoading(true);
    setError(null);
    setRates([]);
    setSaleCoverage(null);
    setShowFasterOptions(false);
    setSelectedRate(null);
    try {
      const data = await getShippingRates({
        shopify_order_id: shipment.shopify_order_id,
        ship_from_name: shipFrom.name,
        ship_from_phone: shipFrom.phone,
        ship_from_address_line1: shipFrom.address_line1,
        ship_from_address_line2: shipFrom.address_line2,
        ship_from_city: shipFrom.city,
        ship_from_state: shipFrom.state,
        ship_from_postal_code: shipFrom.postal_code,
        ship_from_country: shipFrom.country,
        recipient_address_line1: recipient.address_line1,
        recipient_city: recipient.city,
        recipient_state: recipient.state,
        recipient_postal_code: recipient.postal_code,
        recipient_country: recipient.country,
        weight,
        weight_unit: weightUnit,
        width: dimensions.width,
        height: dimensions.height,
        depth: dimensions.depth,
        dimension_unit: dimensionUnit,
        packaging_type: packagingType,
        insurance: extraInsurance,
      });
      if (data.rates && data.rates.some(rate => rate.covered)) {
        setRates(data.rates);
        setSaleCoverage(data.sale_coverage || null);
      } else {
        toast.warning('No shipping rates available for this destination');
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch shipping rates';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching shipping rates:', err);
    } finally {
      setRatesLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedRate) {
      toast.error('Please select a shipping rate');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const data = await createShippingLabel({
        recipient_name: recipient.name,
        recipient_phone: recipient.phone,
        recipient_email: recipient.email,
        recipient_address_line1: recipient.address_line1,
        recipient_address_line2: recipient.address_line2,
        recipient_city: recipient.city,
        recipient_state: recipient.state,
        recipient_postal_code: recipient.postal_code,
        recipient_country: recipient.country,
        ship_from_name: shipFrom.name,
        ship_from_phone: shipFrom.phone,
        ship_from_address_line1: shipFrom.address_line1,
        ship_from_address_line2: shipFrom.address_line2,
        ship_from_city: shipFrom.city,
        ship_from_state: shipFrom.state,
        ship_from_postal_code: shipFrom.postal_code,
        ship_from_country: shipFrom.country,
        weight,
        weight_unit: weightUnit,
        width: dimensions.width,
        height: dimensions.height,
        depth: dimensions.depth,
        dimension_unit: dimensionUnit,
        packaging_type: packagingType,
        reference: shipment.shopify_order_id,
        notes: '',
        insurance: extraInsurance,
        rate_id: selectedRate.id,
      });
      setLabelUrl(data.label_url || null);
      toast.success('Shipping label created successfully');
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to purchase shipping label';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error purchasing shipping label:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showRates = coveredRates.length > 0 && !labelUrl;

  return (
    <div className={styles.modalOverlay}>
      <div ref={modalRef} className={styles.modalContent} role="dialog" aria-modal="true" aria-labelledby="ship-artwork-title">
        <button
          onClick={onClose}
          className={styles.closeButton}
          aria-label="Close"
        >
          ×
        </button>

        <div className={styles.modalHeader}>
          <h2 id="ship-artwork-title">{labelUrl ? 'Label ready' : showRates ? 'Choose a shipping rate' : 'Ship artwork'}</h2>
          <div className={styles.artworkInfo}>
            <img 
              src={shipment.image_url} 
              alt={shipment.title} 
              className={styles.artworkImage}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/placeholder-artwork.png';
              }}
            />
            <div>
              <div className={styles.artworkTitle}>{shipment.title}</div>
              <div className={styles.orderId}>Order ID: {shipment.shopify_order_id}</div>
            </div>
          </div>
        </div>

        <div className={`${styles.modalBody} ae-dialog-scroll`} ref={bodyRef}>
        {validationError && (
          <div className={styles.validationError}>
            {validationError}
          </div>
        )}

        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}

        {!showRates && !labelUrl && (
        <>
        <div className={styles.formSection}>
          <h3>Recipient Info</h3>
          {recipientLocked && (
            <p className={styles.rateSummary}>
              This is the collector's address from the sale. Contact support if it needs to change.
            </p>
          )}
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Recipient Name *</label>
              <input 
                value={recipient.name} 
                readOnly={recipientLocked} onChange={e => setRecipient(r => ({ ...r, name: e.target.value }))}
                className={formErrors.recipientName ? styles.inputError : ''}
              />
              {formErrors.recipientName && <span className={styles.errorText}>{formErrors.recipientName}</span>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="recipient_phone">Recipient Phone:</label>
              <input id="recipient_phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="000-000-0000" value={recipient.phone} readOnly={recipientLocked} onChange={e => setRecipient(r => ({ ...r, phone: e.target.value }))} maxLength={20} />
            </div>
            <div className={styles.formGroup}>
              <label>Recipient Email:</label>
              <input value={recipient.email} readOnly={recipientLocked} onChange={e => setRecipient(r => ({ ...r, email: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>Recipient Address:</label>
              <input value={recipient.address_line1} readOnly={recipientLocked} onChange={e => setRecipient(r => ({ ...r, address_line1: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>City:</label>
              <input value={recipient.city} readOnly={recipientLocked} onChange={e => setRecipient(r => ({ ...r, city: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>State:</label>
              <input value={recipient.state} readOnly={recipientLocked} onChange={e => setRecipient(r => ({ ...r, state: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>Postal Code:</label>
              <input value={recipient.postal_code} readOnly={recipientLocked} onChange={e => setRecipient(r => ({ ...r, postal_code: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>Country:</label>
              <input value={recipient.country} readOnly={recipientLocked} onChange={e => setRecipient(r => ({ ...r, country: e.target.value }))} />
            </div>
          </div>
        </div>

        <div className={styles.formSection}>
          <h3>Ship From Info</h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Ship From Name:</label>
              <input value={shipFrom.name} onChange={e => setShipFrom(r => ({ ...r, name: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="ship_from_phone">Ship From Phone:</label>
              <input id="ship_from_phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="000-000-0000" value={shipFrom.phone} onChange={e => setShipFrom(r => ({ ...r, phone: e.target.value }))} maxLength={20} />
            </div>
            <div className={styles.formGroup}>
              <label>Ship From Address:</label>
              <input value={shipFrom.address_line1} onChange={e => setShipFrom(r => ({ ...r, address_line1: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>City:</label>
              <input value={shipFrom.city} onChange={e => setShipFrom(r => ({ ...r, city: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>State:</label>
              <input value={shipFrom.state} onChange={e => setShipFrom(r => ({ ...r, state: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>Postal Code:</label>
              <input value={shipFrom.postal_code} onChange={e => setShipFrom(r => ({ ...r, postal_code: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>Country:</label>
              <input value={shipFrom.country} onChange={e => setShipFrom(r => ({ ...r, country: e.target.value }))} />
            </div>
          </div>
        </div>

        <div className={styles.formSection}>
          <h3>Package Details</h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Dimensions (W x H x D):</label>
              <input ref={blockWheelOnNumber} type="number" min="0" step="any" value={dimensions.width} onChange={e => setDimensions(d => ({ ...d, width: e.target.value }))} placeholder="Width" />
              <input ref={blockWheelOnNumber} type="number" min="0" step="any" value={dimensions.height} onChange={e => setDimensions(d => ({ ...d, height: e.target.value }))} placeholder="Height" />
              <input ref={blockWheelOnNumber} type="number" min="0" step="any" value={dimensions.depth} onChange={e => setDimensions(d => ({ ...d, depth: e.target.value }))} placeholder="Depth" />
              <select value={dimensionUnit} onChange={e => setDimensionUnit(e.target.value)}>
                <option value="in">in</option>
                <option value="cm">cm</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Weight:</label>
              <input ref={blockWheelOnNumber} type="number" min="0" step="any" value={weight} onChange={e => setWeight(e.target.value)} />
              <select value={weightUnit} onChange={e => setWeightUnit(e.target.value)}>
                <option value="lb">lb</option>
                <option value="kg">kg</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Packaging Type:</label>
              <select value={packagingType} onChange={e => setPackagingType(e.target.value)}>
                {packagingOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Insurance Coverage (ParcelGuard):</label>
              <div className={styles.insuranceSection}>
                <div className={styles.parcelGuardInfo}>
                  <span className={styles.parcelGuardBadge}>🛡️ Powered by ParcelGuard</span>
                  <span>Professional shipping insurance for your valuable artwork</span>
                </div>
                <div className={styles.extraInsurance}>
                  <label>Select Insurance Coverage:</label>
                  <select 
                    value={extraInsurance} 
                    onChange={e => setExtraInsurance(Number(e.target.value))}
                    className={styles.insuranceSelect}
                  >
                    <option value={0}>Standard $100 coverage</option>
                    {[200, 500, 1000, 2000, 3000, 5000, 10000]
                      .filter(amount => amount <= maxInsurance)
                      .map(amount => (
                        <option key={amount} value={amount}>${amount.toLocaleString()} coverage</option>
                      ))}
                  </select>
                  {extraInsurance > 0 && (
                    <div className={styles.totalCoverage}>
                      <strong>🛡️ ${extraInsurance} ParcelGuard Protection</strong>
                      <div className={styles.insuranceFeatures}>
                        <small>• Full replacement value coverage<br/>• Damage and theft protection<br/>• Easy claims process</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
        )}

        {showRates && (
            <div className={styles.ratesSection}>
              <p className={styles.rateSummary}>
                To {recipient.city || 'the collector'}
                {recipient.state ? `, ${recipient.state}` : ''}
                {' · '}
                {weight} {weightUnit}
                {' · '}
                {`ParcelGuard $${saleCoverage?.insurance || extraInsurance || 100}`}
              </p>
              {saleCoverage && (
                <p className={styles.rateSummary}>
                  Shipping is covered by this sale. You won't be charged for the label.
                </p>
              )}
              {visibleRates.map(rate => (
                <div
                  key={rate.id}
                  className={`${styles.rateCard} ${selectedRate?.id === rate.id ? styles.selectedRate : ''}`}
                  onClick={() => setSelectedRate(rate)}
                >
                  <input
                    type="radio"
                    name="rate"
                    value={rate.id}
                    checked={selectedRate?.id === rate.id}
                    onChange={() => setSelectedRate(rate)}
                  />
                  <div className={styles.rateInfo}>
                    <div className={styles.carrierInfo}>
                      <b>{formatRateName(rate)}</b>
                      <span className={styles.estimatedDays}>Est. {rate.est_days || '?'} days</span>
                    </div>
                    <div className={styles.priceInfo}>
                      <div className={styles.price}>Covered</div>
                    </div>
                  </div>
                </div>
              ))}
              {(hiddenRateCount > 0 || showFasterOptions) && (
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => {
                    if (showFasterOptions && selectedRate && !selectedRate.recommended) setSelectedRate(null);
                    setShowFasterOptions(show => !show);
                  }}
                >
                  {showFasterOptions ? 'Show fewer options' : `Show faster options (${hiddenRateCount})`}
                </button>
              )}
            </div>
        )}

        {labelUrl && (
            <div className={styles.successSection}>
              <div className={styles.successMessage}>
                <span className={styles.checkmark}>✓</span>
                Label purchased successfully!
              </div>
              <a
                href={labelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.downloadButton}
              >
                Download Shipping Label
              </a>
            </div>
        )}
        </div>

        <div className={styles.modalFooter}>
          {!showRates && !labelUrl && (
            <button
              onClick={handleGetRates}
              className={styles.primaryButton}
              disabled={ratesLoading}
            >
              {ratesLoading ? (
                <>
                  <span className={styles.spinner}></span>
                  Getting Rates...
                </>
              ) : 'Get Shipping Rates'}
            </button>
          )}

          {showRates && (
            <div className={styles.footerActions}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => {
                  setRates([]);
                  setSelectedRate(null);
                  setError(null);
                }}
              >
                Edit details
              </button>
              <button
                onClick={handlePurchase}
                className={styles.primaryButton}
                disabled={!selectedRate || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className={styles.spinner}></span>
                    Creating label...
                  </>
                ) : 'Create label'}
              </button>
            </div>
          )}

          {labelUrl && (
            <button type="button" className={styles.primaryButton} onClick={onClose}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShipArtworkModal; 